import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';

const features = [
  {
    title: 'Python 题目库',
    desc: '丰富的 Python 编程题目，涵盖基础语法到高级算法，适合中学课程体系。',
  },
  {
    title: 'AI 智能辅导',
    desc: 'AI 助手实时分析代码，提供个性化解释和代码变体练习，让学习不再孤独。',
  },
  {
    title: '间隔重复学习',
    desc: '基于艾宾浩斯遗忘曲线，科学安排复习计划，巩固每一个知识点。',
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  return (
    <div className='min-h-screen bg-apple-black text-white'>
      {/* Public nav */}
      <nav
        className='sticky top-0 z-50 h-12 flex items-center'
        style={{
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        }}
      >
        <div className='max-w-[980px] mx-auto w-full px-6 flex items-center justify-between'>
          <span className='text-white font-semibold text-[17px] tracking-tight'>CodePilot</span>
          <div className='flex items-center gap-5'>
            <Link
              to='/login'
              className='text-white/70 text-[12px] hover:text-white transition-colors'
            >
              登录
            </Link>
            <Link
              to='/register'
              className='bg-apple-blue text-white text-[12px] px-3 py-1 rounded-full hover:bg-apple-blue-hover transition-colors'
            >
              注册
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero — black */}
      <section className='py-36 text-center'>
        <div className='max-w-[740px] mx-auto px-6'>
          <p className='text-apple-blue text-[12px] font-semibold uppercase tracking-widest mb-5'>
            专为中学生设计
          </p>
          <h1
            className='font-semibold text-white mb-7'
            style={{ fontSize: '3.5rem', lineHeight: 1.07, letterSpacing: '-0.02em' }}
          >
            AI 驱动的 Python
            <br />
            学习平台
          </h1>
          <p
            className='text-white/70 font-light max-w-lg mx-auto mb-10'
            style={{ fontSize: '1.3125rem', lineHeight: 1.47, letterSpacing: '-0.374px' }}
          >
            CodePilot 为中学信息技术课学生提供智能编程练习、实时评测和学习路径规划。
          </p>
          <div className='flex items-center justify-center gap-5 flex-wrap'>
            <Link
              to='/register'
              className='bg-apple-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-apple-blue-hover transition-colors active:scale-[0.98] text-[1.0625rem]'
            >
              开始学习
            </Link>
            <Link
              to='/login'
              className='text-apple-blue-dark-link text-[1.0625rem] hover:underline inline-flex items-center gap-0.5'
            >
              已有账号 ›
            </Link>
          </div>
        </div>
      </section>

      {/* Features — light gray */}
      <section className='py-24 bg-apple-light-gray'>
        <div className='max-w-[980px] mx-auto px-6'>
          <h2
            className='font-semibold text-apple-near-black text-center mb-16'
            style={{ fontSize: '2.5rem', lineHeight: 1.1 }}
          >
            为什么选择 CodePilot
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
            {features.map((f) => (
              <div key={f.title} className='bg-white rounded-2xl p-8 shadow-card-sm'>
                <h3
                  className='font-semibold text-apple-near-black mb-3'
                  style={{ fontSize: '1.3125rem', lineHeight: 1.19 }}
                >
                  {f.title}
                </h3>
                <p
                  className='text-apple-gray'
                  style={{ fontSize: '0.9375rem', lineHeight: 1.47, letterSpacing: '-0.374px' }}
                >
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — dark */}
      <section className='py-28 bg-apple-black text-center'>
        <div className='max-w-[980px] mx-auto px-6'>
          <h2
            className='font-semibold text-white mb-6'
            style={{ fontSize: '2.5rem', lineHeight: 1.1 }}
          >
            开始你的学习之旅
          </h2>
          <p className='text-white/60 mb-10' style={{ fontSize: '1.0625rem' }}>
            免费注册，立即开始练习。
          </p>
          <Link
            to='/register'
            className='inline-block bg-apple-blue text-white px-8 py-3 rounded-lg font-medium hover:bg-apple-blue-hover transition-colors active:scale-[0.98] text-[1.0625rem]'
          >
            免费注册
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className='border-t border-white/10 bg-apple-black py-8'>
        <div className='max-w-[980px] mx-auto px-6 text-center'>
          <p className='text-white/30' style={{ fontSize: '0.75rem' }}>
            © 2026 CodePilot. 保留所有权利。
          </p>
        </div>
      </footer>
    </div>
  );
}
